import type { MouseEvent } from "react";

export type LinkDisplayType = "bookmark" | "embed" | "url";

const YOUTUBE_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
const VIMEO_RE = /vimeo\.com\/(\d+)/;

export function getYouTubeId(url: string): string | null {
  const m = url.match(YOUTUBE_RE);
  return m ? m[1] : null;
}

export function getVimeoId(url: string): string | null {
  const m = url.match(VIMEO_RE);
  return m ? m[1] : null;
}

export function isEmbeddableVideo(url: string): boolean {
  return !!getYouTubeId(url) || !!getVimeoId(url);
}

export function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// Builds the HTML fragment inserted into note content for a given link and
// display type. contenteditable="false" makes the block atomic inside the
// parent contentEditable region so it is not edited character by character,
// and its own clicks/iframe controls keep working normally.
export function buildLinkHtml(url: string, type: LinkDisplayType): string {
  const safeUrl = url.replace(/"/g, "&quot;");
  if (type === "embed") {
    const ytId = getYouTubeId(url);
    const vimeoId = getVimeoId(url);
    const src = ytId
      ? `https://www.youtube.com/embed/${ytId}`
      : vimeoId
        ? `https://player.vimeo.com/video/${vimeoId}`
        : null;
    if (src) {
      return `<div contenteditable="false" data-link-block data-url="${safeUrl}" style="position:relative;width:100%;padding-top:56.25%;border-radius:16px;overflow:hidden;margin:8px 0;background:#000;"><iframe src="${src}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;"></iframe><button type="button" class="link-delete-btn" aria-label="Remove video" style="position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:9999px;background:rgba(0,0,0,0.6);color:#fff;border:none;cursor:pointer;z-index:2;display:flex;align-items:center;justify-content:center;padding:0;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"></line><line x1="19" y1="5" x2="5" y2="19"></line></svg></button></div><p><br></p>`;
    }
  }
  if (type === "bookmark") {
    const domain = getDomain(url);
    return `<div contenteditable="false" data-link-block data-href="${safeUrl}" style="position:relative;display:block;border:1px solid var(--border, #333);border-radius:16px;padding:12px 40px 12px 14px;margin:8px 0;cursor:pointer;"><div style="font-size:12px;opacity:0.6;margin-bottom:2px;">${domain}</div><div style="font-size:14px;font-weight:500;word-break:break-all;">${safeUrl}</div><button type="button" class="link-delete-btn" aria-label="Remove bookmark" style="position:absolute;top:8px;right:8px;width:22px;height:22px;border-radius:9999px;background:rgba(128,128,128,0.25);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"></line><line x1="19" y1="5" x2="5" y2="19"></line></svg></button></div><p><br></p>`;
  }
  return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline;">${safeUrl}</a>`;
}

// Attach to a contentEditable container onClick handler. Intercepts clicks on
// rendered links and opens them in a new tab instead of the default
// contentEditable behavior, which just places a text cursor.
export function handleContentLinkClick(e: MouseEvent<HTMLDivElement>) {
  const target = e.target as HTMLElement;

  const deleteBtn = target.closest(".link-delete-btn") as HTMLElement | null;
  if (deleteBtn) {
    e.preventDefault();
    e.stopPropagation();
    const block = deleteBtn.closest("[data-link-block]");
    if (block) {
      block.remove();
      e.currentTarget.dispatchEvent(new Event("input", { bubbles: true }));
    }
    return;
  }

  const bookmarkBlock = target.closest("[data-href]") as HTMLElement | null;
  if (bookmarkBlock) {
    e.preventDefault();
    window.open(bookmarkBlock.getAttribute("data-href")!, "_blank", "noopener,noreferrer");
    return;
  }

  const anchor = target.closest("a");
  if (anchor && anchor.getAttribute("href")) {
    e.preventDefault();
    window.open(anchor.href, "_blank", "noopener,noreferrer");
  }
}
